import React, { useState } from "react";
import Pagination from "../../../components/Pagination/Pagination";
import {
  formatLabel,
} from "../../../components/Constants/Constants";
import AppLoader from "../../../components/AppLoader/appLoader";
import "./CounterPartyIssues.css";
import { useCounterPartyIssues } from "../../../customHooks/CounterPartyIssues/CounterPartyIssues/useCounterPartyIssues";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CopyIcon from "../../../components/CopyIcon/CopyIcon";
import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UnAuthorized from "../../UnAuthorized/UnAuthorized";
import usePermission from "../../../utils/usePermission";

const columnInfo = {
  issueSource: "The entity that originally reported the issue.",
  resolution: "The final resolution proposed or accepted (e.g., REFUND).",
  expectedResponse:
    "The deadline for the *next action* or response from the counter-party.",
  expectedResolution:
    "The final deadline by which the issue is expected to be fully resolved.",
  lastAction:
    "The most recent action taken by any party in the issue timeline.",
};

const CounterPartyIssues = () => {
  const [activeTab, setActiveTab] = useState("issues");
  const navigate = useNavigate();
  const {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    analytics,
  } = useCounterPartyIssues(activeTab);

  const canViewIssues = usePermission("ISSUE_LISTING_VIEW");
  const canViewIssueDetails = usePermission("ISSUE_DETAILS_VIEW");


  return (
    <>
      {canViewIssues ? (
        <div className="m-4">
          {/* --- Tabs --- */}
          <div className="d-flex gap-3 mb-3">
            <button
              className={`btn ${
                activeTab === "issues"
                  ? "text-white fw-bold" // Active classes
                  : "border-0 border-bottom text-muted" // Inactive classes
              }`}
              style={
                activeTab === "issues"
                  ? { backgroundColor: "#38d3f1", border: "none" } // Active style
                  : {} // No style for inactive
              }
              onClick={() => setActiveTab("issues")}
            >
              Issues
            </button>
            <button
              className={`btn ${
                activeTab === "grievances"
                  ? "text-white fw-bold" // Active classes
                  : "border-0 border-bottom text-muted" // Inactive classes
              }`}
              style={
                activeTab === "grievances"
                  ? { backgroundColor: "#38d3f1", border: "none" } // Active style
                  : {} // No style for inactive
              }
              onClick={() => setActiveTab("grievances")}
            >
              Escalated To Grievances
            </button>
          </div>

          <p className="text-muted small mb-3">
            This page provides a consolidated view of all reported issues and
            grievances. Please use the filters below to search for specific
            items and click on an Issue ID to view detailed information and
            history.
          </p>

          {analytics && Object.keys(analytics).length > 0 && (
            <div className="row mb-4">
              {" "}
              <div className="col">
                <div className="p-2 bg-white shadow-sm rounded text-center">
                  <h6 className="text-muted mb-0 small">Open Issues</h6>
                  <h4 className="fw-bold text-warning mb-0">
                    {analytics.open || 0}
                  </h4>
                </div>
              </div>
              <div className="col">
                {" "}
                <div className="p-2 bg-white shadow-sm rounded text-center">
                  <h6 className="text-muted mb-0 small">Processing</h6>{" "}
                  <h4 className="fw-bold text-primary mb-0">
                    {" "}
                    {analytics.processing || 0}
                  </h4>
                </div>
              </div>
              <div className="col">
                <div className="p-2 bg-white shadow-sm rounded text-center">
                  <h6 className="text-muted mb-0 small">Resolved</h6>
                  <h4 className="fw-bold text-success mb-0">
                    {analytics.resolved || 0}
                  </h4>
                </div>
              </div>
              <div className="col">
                <div className="p-2 bg-white shadow-sm rounded text-center">
                  <h6 className="text-muted mb-0 small">Escalated</h6>
                  <h4 className="fw-bold text-danger mb-0">
                    {analytics.escalated || 0}
                  </h4>
                </div>
              </div>
              <div className="col">
                <div className="p-2 bg-white shadow-sm rounded text-center">
                  <h6 className="text-muted mb-0 small">Closed</h6>
                  <h4 className="fw-bold text-secondary mb-0">
                    {" "}
                    {analytics.closed || 0}
                  </h4>
                </div>
              </div>
            </div>
          )}

          <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
            <div className="row gx-4 gy-3 align-items-end flex-wrap">
              {/* Search By */}
              <div className="col-auto">
                <label className="form-label mb-1">Search By</label>
                <div className="input-group" style={{ minWidth: "400px" }}>
                  <select
                    className="form-select"
                    style={{ maxWidth: "150px" }}
                    value={filters.search_by}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        search_by: e.target.value,
                        search_value: "",
                      }))
                    }
                  >
                    <option value="">Select Field</option>
                    <option value="issueId">Issue ID</option>
                    <option value="transaction_id">Transaction ID</option>
                    <option value="complainant_name">Issue Source</option>
                  </select>

                  <input
                    type="text"
                    className="form-control"
                    style={{ fontSize: "12px", padding: "none" }}
                    placeholder={`Enter ${
                      filters.search_by
                        ? filters.search_by.replace("_", " ")
                        : "value"
                    }`}
                    value={filters.search_value}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        search_value: e.target.value,
                      }))
                    }
                    disabled={!filters.search_by}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="col-auto">
                <label className="form-label mb-1">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  <option value="">All</option>
                  {filterOptions?.status?.map((s) => (
                    <option key={s} value={s}>
                      {s
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Issue Category */}
              <div className="col-auto">
                <label className="form-label mb-1">Issue Category</label>
                <select
                  className="form-select"
                  value={filters.issue_category}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      issue_category: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  {filterOptions?.issue_category?.map((s) => (
                    <option key={s} value={s}>
                      {s
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-auto">
                <label className="form-label mb-1">Issue Domain</label>
                <select
                  className="form-select"
                  value={filters.issue_domain}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, issue_domain: e.target.value }))
                  }
                >
                  <option value="">All</option>
                  {/* Note: .issue_domain is an array of objects */}
                  {filterOptions?.issue_domain?.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Resolution Type */}
              <div className="col-auto">
                <label className="form-label mb-1">Resolution Type</label>
                <select
                  className="form-select"
                  value={filters.resolution_type}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      resolution_type: e.target.value,
                    }))
                  }
                >
                  <option value="">All</option>
                  {filterOptions?.resolution_type?.map((s) => (
                    <option key={s} value={s}>
                      {s
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* SLA Breach */}
              <div className="col-auto">
                <label className="form-label mb-1">SLA Breach</label>
                <select
                  className="form-select"
                  value={filters.sla_breach}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, sla_breach: e.target.value }))
                  }
                >
                  <option value="">All</option>
                  {filterOptions?.sla_breach?.map((s) => (
                    <option key={s} value={s}>
                      {s
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Created From */}
              <div className="col-auto">
                <label className="form-label mb-1">Created From</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ fontSize: "12px", padding: "none" }}
                  value={filters.created_from}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, created_from: e.target.value }))
                  }
                />
              </div>

              {/* Created To */}
              <div className="col-auto">
                <label className="form-label mb-1">Created To</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ fontSize: "12px", padding: "none" }}
                  value={filters.created_to}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, created_to: e.target.value }))
                  }
                />
              </div>

              {/* Clear Filters Button */}
              <div className="col d-flex">
                <button
                  type="button"
                  className="btn btn-primary no-hover-btn"
                  style={{ padding: "5px 8px" }}
                  onClick={() =>
                    setFilters({
                      status: "",
                      issueId: "",
                      issue_category: "",
                      issue_level: "",
                      issue_domain: "",
                      resolution_type: "",
                      sla_breach: "",
                      created_from: "",
                      created_to: "",
                      search_by: "",
                      search_value: "",
                      sort_order: "desc",
                    })
                  }
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          <>
            {loading ? (
              <AppLoader />
            ) : (
              <div className="table-responsive mt-4">
                <table className="table table-bordered table-hover align-middle text-center">
                  <thead className="table-light text-center">
                    <tr>
                      <th>Issue ID</th>
                      <th>Transaction ID</th>
                      <th>
                        Issue Source
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{columnInfo.issueSource}</Tooltip>}
                        >
                          <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th>Issue Category</th>
                      <th>Issue Description</th>

                      <th>Status</th>
                      <th>
                        Resolution
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{columnInfo.resolution}</Tooltip>}
                        >
                          <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th>
                        Expected Response
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>{columnInfo.expectedResponse}</Tooltip>
                          }
                        >
                          <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th>
                        Expected Resolution
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>{columnInfo.expectedResolution}</Tooltip>
                          }
                        >
                          <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th>
                        Last Action
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{columnInfo.lastAction}</Tooltip>}
                        >
                          <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            sort_order: f.sort_order === "asc" ? "desc" : "asc",
                          }))
                        }
                      >
                        Created At{" "}
                        {filters.sort_order === "asc" ? (
                          <span>&uarr;</span>
                        ) : (
                          <span>&darr;</span>
                        )}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            sort_order: f.sort_order === "asc" ? "desc" : "asc",
                          }))
                        }
                      >
                        Updated At{" "}
                        {filters.sort_order === "asc" ? (
                          <span>&uarr;</span>
                        ) : (
                          <span>&darr;</span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? (
                      data.map((issue, i) => (
                        <tr key={i}>
                          <td>
                            <div className="copy-cell">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-issue-${i}`}>
                                    {issue.issue_id}
                                  </Tooltip>
                                }
                              >
                                <button
                                  className="btn btn-link btn-sm p-0 truncate-cell"
                                  onClick={() => {
                                    if (canViewIssueDetails) {
                                      navigate(
                                        `/issues/buyer/${issue.issue_id}`
                                      );
                                    } else {
                                      alert(
                                        "You do not have permission to view issue details."
                                      );
                                    }
                                  }}
                                  disabled={!canViewIssueDetails} // Disable clicking visually
                                  style={{
                                    cursor: canViewIssueDetails
                                      ? "pointer"
                                      : "not-allowed",
                                    opacity: canViewIssueDetails ? 1 : 0.5,
                                  }}
                                >
                                  {issue.issue_id}
                                </button>
                              </OverlayTrigger>

                              <CopyIcon textToCopy={issue.issue_id} />
                            </div>
                          </td>

                          <td>
                            <div className="copy-cell">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-tx-${i}`}>
                                    {issue.transaction_id}
                                  </Tooltip>
                                }
                              >
                                <span className="truncate-cell">
                                  {issue.transaction_id}
                                </span>
                              </OverlayTrigger>
                              <CopyIcon textToCopy={issue.transaction_id} />
                            </div>
                          </td>
                          <td>
                            <div className="copy-cell">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-tx-${i}`}>
                                    {issue.complainant_name}
                                  </Tooltip>
                                }
                              >
                                <span className="truncate-cell">
                                  {issue.complainant_name}
                                </span>
                              </OverlayTrigger>
                              <CopyIcon textToCopy={issue.complainant_name} />
                            </div>
                          </td>

                          <td>{issue.issue_category}</td>
                          <td>{issue.issue_description}</td>
                          <td>{issue.status}</td>
                          <td>{formatLabel(issue.resolution)}</td>
                          <td>{issue.expected_response}</td>
                          <td>{issue.expected_resolution}</td>
                          <td>{formatLabel(issue.last_action)}</td>
                          <td>{issue.CreatedAt}</td>
                          <td>{issue.updated_at}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={12}
                          className="text-center py-4 text-gray-500"
                        >
                          No {activeTab} Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* --- Pagination --- */}
            {totalCount > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                startItem={(currentPage - 1) * itemsPerPage + 1}
                endItem={Math.min(currentPage * itemsPerPage, totalCount)}
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        </div>
      ) : (
        <UnAuthorized />
      )}
    </>
  );
};

export default CounterPartyIssues;
